import {
  useWSModalCustom,
  useWSSnackbar,
  WSForm,
  WSInputText,
  WSList,
  WSModal,
  WSModalCustomProps
} from "@wingspanhq/fe-component-library";
import { WorkDefinition, WorkItem } from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";
import * as Yup from "yup";
import { queriesWorkItems } from "@query";
import { ErrorAlert } from "@components/ErrorAlert";
import { FormPartialAttributes } from "../../components/FormPartialAttributes";
import { AcceptedValuesResources } from "../../components/FormPartialAttributes/helpers";
import { getAttributeValidators } from "../../components/FormPartialAttributes/validation";
import { PublicCommentField } from "../../components/PublicCommentField";
import { stripNullAttributes } from "../../utils/stripNullAttributes";

type Props = {
  workItem: WorkItem;
  workDefinition: WorkDefinition;
  resources: AcceptedValuesResources;
  contractorName?: string;
} & WSModalCustomProps<any>;

export const ModalEditItemPayer: React.FC<Props> = ({
  workItem,
  workDefinition,
  resources,
  contractorName,
  ...modalProps
}) => {
  const validationSchema = useMemo(() => {
    const attributeValidators = getAttributeValidators(
      workDefinition.attributeDefinitions ?? []
    );

    return Yup.object().shape({
      comment: Yup.string().required("A comment is required when editing"),
      ...attributeValidators
    });
  }, [workDefinition]);

  const [mutate, meta] = queriesWorkItems.updateWorkItem.useMutation();

  const { openSnackbar } = useWSSnackbar();

  const commentDescription = contractorName
    ? `Comment is visible to both you and the contractor. Please provide specific details that will inform ${contractorName} about the edits.`
    : "Comment is visible to both you and the contractor. Please provide specific details about the edits.";

  return (
    <WSForm
      defaultValues={{
        ...(workItem.attributes ?? {}),
        comment: ""
      }}
      validationSchema={validationSchema}
      onSubmit={({ comment, ...attributes }) => {
        mutate(
          {
            workItemId: workItem.id!,
            workItemUpdate: {
              attributes: stripNullAttributes(attributes),
              payerOwnedData: { comment }
            }
          },
          {
            onSuccess: () => {
              modalProps.onClose(undefined);

              openSnackbar({
                icon: {
                  name: "check"
                },
                message: "Work item updated"
              });
            }
          }
        );
      }}
    >
      <WSModal
        title="Edit item"
        actions={{
          alignment: "fill",
          buttons: [
            {
              kind: "Secondary",
              label: "Cancel",
              type: "button",
              onClick: () => {
                modalProps.onClose(undefined);
              }
            },
            {
              kind: "Primary",
              label: "Save changes",
              loading: meta.isLoading
            }
          ]
        }}
        {...modalProps}
      >
        <WSList gap="L">
          <WSInputText
            disabled
            label="Work type"
            required
            value={workDefinition.name || ""}
            onChange={() => {}}
          />

          <FormPartialAttributes
            attributeDefinitions={workDefinition.attributeDefinitions ?? []}
            resources={resources}
          />

          <PublicCommentField
            label="Comment *"
            description={commentDescription}
            placeholder="Provide specific details about the edits"
          />

          <ErrorAlert error={meta.error} context="WorkItem" />
        </WSList>
      </WSModal>
    </WSForm>
  );
};

export const useModalEditItemPayer = () => useWSModalCustom(ModalEditItemPayer);
